/**
 * MvbFindingsPanel (P369) -- open findings across the MVB scope
 * (MVB gap-analysis F5).
 *
 * "What is hurting the minimum viable bank right now" -- worst-first,
 * each row naming the MVB IBSes it reaches. Findings on configuration
 * items surface through the service they run.
 */
import { useTranslation } from "react-i18next";
import { listOf } from "shared/i18n/listOf";
import { Bdi } from "shared/components/Bdi";
import { useQuery } from "@tanstack/react-query";

import { api } from "shared/api/client";
import { FindingLink } from "shared/components/FindingLink";

interface MvbFindingRow {
  finding_id: string;
  title: string;
  severity: string;
  entity_kind: string;
  entity_name: string;
  via_ci: string | null;
  mvb_ibs: string[];
  raised_at: string | null;
}

interface MvbFindingsOut {
  generated_at: string;
  summary: {
    total: number;
    critical: number;
    high: number;
    ibs_affected: number;
    /** Round 17 P1 -- of those, the admitted services. */
    ibs_affected_admitted?: number;
    /** Round 18 P7 -- the scope's admitted functions and candidates. */
    ibs_affected_admitted_functions?: number;
    ibs_affected_candidates?: number;
  };
  rows: MvbFindingRow[];
}

const SEVERITY_CLASS: Record<string, string> = {
  critical: "bg-rose-100 text-rose-800",
  high: "bg-amber-100 text-amber-800",
  medium: "bg-slate-100 text-slate-700",
  low: "bg-slate-100 text-slate-500",
};

const MAX_ROWS = 12;

export function MvbFindingsPanel(): JSX.Element | null {
  const { t } = useTranslation();
  const { data } = useQuery({
    queryKey: ["twin", "mvb-findings"],
    queryFn: () => api.get<MvbFindingsOut>("/v1/twin/mvb/findings"),
    refetchInterval: 60_000,
  });
  // Shape guard: a panel that renders beside other queries must not
  // crash on an unexpected payload -- degrade to hidden instead.
  if (!data || typeof data.summary?.total !== "number") return null;
  if (data.summary.total === 0 || !Array.isArray(data.rows)) return null;
  // Round 18 P7 -- the scope named by class and admission when the reply
  // carries the split; an older reply keeps "N of them admitted".
  const sm = data.summary;
  const scopeSplit =
    typeof sm.ibs_affected_admitted_functions === "number" &&
    typeof sm.ibs_affected_candidates === "number"
      ? [
          (sm.ibs_affected_admitted ?? 0) > 0 &&
            t("screens.mvbFindingsPanel.scopeAdmittedServices", {
              count: sm.ibs_affected_admitted ?? 0,
            }),
          sm.ibs_affected_admitted_functions > 0 &&
            t("screens.mvbFindingsPanel.scopeAdmittedFunctions", {
              count: sm.ibs_affected_admitted_functions,
            }),
          sm.ibs_affected_candidates > 0 &&
            t("screens.mvbFindingsPanel.scopeCandidates", {
              count: sm.ibs_affected_candidates,
            }),
        ].filter((x): x is string => typeof x === "string")
      : null;

  return (
    <section data-testid="mvb-findings-panel" className="space-y-2">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
        {t("screens.mvbFindingsPanel.hurtingTheMvbNow")}
      </h2>
      <p className="text-xs text-slate-500">
        {/* Round 17 H40 -- one plural-aware sentence, not fragments
            around bare numbers ("29 open finding(s)", "MVB IBS(es)"). */}
        {t("screens.mvbFindingsPanel.summary", {
          count: data.summary.total,
          critical: data.summary.critical,
          high: data.summary.high,
          affected: scopeSplit
            ? t("screens.mvbFindingsPanel.affectedScope", {
                count: data.summary.ibs_affected,
                parts: listOf(scopeSplit),
              })
            : t("screens.mvbFindingsPanel.affected", {
                count: data.summary.ibs_affected,
              }),
        })}
        {!scopeSplit &&
          typeof data.summary.ibs_affected_admitted === "number" &&
          data.summary.ibs_affected_admitted !== data.summary.ibs_affected && (
            <>
              {" "}
              {t("screens.mvbFindingsPanel.admittedOfThem", {
                count: data.summary.ibs_affected_admitted,
              })}
            </>
          )}
      </p>
      <ul className="space-y-1">
        {data.rows.slice(0, MAX_ROWS).map((r) => (
          <li
            key={r.finding_id}
            className="flex flex-wrap items-baseline gap-x-2 rounded border border-paper-2 bg-white px-3 py-1.5 text-sm"
          >
            <span
              className={`rounded px-1.5 py-0.5 text-xs font-semibold ${
                SEVERITY_CLASS[r.severity] ?? "bg-slate-100 text-slate-600"
              }`}
            >
              {r.severity}
            </span>
            <FindingLink
              id={r.finding_id}
              title={r.title}
              className="font-medium text-slate-900"
            />
            <span className="text-xs text-slate-500">
              {/* D2 -- a service name and a host name stitched onto a
                  translated word. Reordered, "on X (via Y)" put the
                  hostname first and the preposition last. */}
              {t("screens.mvbFindingsPanel.on")} <Bdi>{r.entity_name}</Bdi>
              {r.via_ci ? (
                <>
                  {" ("}
                  {t("screens.mvbFindingsPanel.via")} <Bdi>{r.via_ci}</Bdi>
                  {")"}
                </>
              ) : null}
            </span>
            <span
              className="ms-auto text-xs text-slate-400"
              title={r.mvb_ibs.join(", ")}
            >
              {/* Round 18 L11 -- the scope holds functions too: Treasury's
                  finding read "reaches 1 MVB IBS". */}
              {t("screens.mvbFindingsPanel.reachesMembers", {
                count: r.mvb_ibs.length,
              })}
            </span>
          </li>
        ))}
      </ul>
      {data.rows.length > MAX_ROWS && (
        <p className="text-xs text-slate-400">
          + {data.rows.length - MAX_ROWS} {t("screens.mvbFindingsPanel.more")}
        </p>
      )}
    </section>
  );
}
